
import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TeamCoach {
  id: string;
  team_id: string;
  coach_id: string;
  role: string;
  is_active: boolean;
  profiles: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
}

export const useTeamCoaches = (teamId: string | null) => {
  const queryClient = useQueryClient();
  const [coaches, setCoaches] = useState<TeamCoach[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchTeamCoaches = useCallback(async () => {
    if (!teamId) {
      setCoaches([]);
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('team_coaches')
        .select(`
          id,
          team_id,
          coach_id,
          role,
          is_active,
          profiles!inner(id, first_name, last_name, email)
        `)
        .eq('team_id', teamId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching team coaches:', error);
        throw error;
      }
      
      setCoaches(data || []);
    } catch (error) {
      console.error('Error fetching team coaches:', error);
      toast.error('Failed to load team coaches');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const addCoach = async (coachId: string, role: string = 'assistant_coach') => {

    if (!teamId) {
      console.error('❌ Cannot add coach: no team ID provided');
      toast.error('Cannot add coach: no team selected');
      return false;
    }

    if (!coachId) {
      console.error('❌ Cannot add coach: no coach ID provided');
      toast.error('Cannot add coach: no coach selected');
      return false;
    }

    try {

      // Check if coach is already assigned to this team
      
      const { data: existingAssignment, error: checkError } = await supabase
        .from('team_coaches')
        .select('id, coach_id, team_id, role, is_active')
        .eq('team_id', teamId)
        .eq('coach_id', coachId)
        .eq('is_active', true);


      if (checkError) {
        console.error('❌ Error checking existing assignment:', checkError);
        toast.error(`Database error: ${checkError.message}`);
        return false;
      }

      // Check if any results were returned (coach already assigned)
      if (existingAssignment && existingAssignment.length > 0) {
        toast.error('This coach is already assigned to this team');
        return false;
      }


      // Insert the new team coach assignment
      const insertData = {
        team_id: teamId,
        coach_id: coachId,
        role: role,
        is_active: true
      };


      const { data, error } = await supabase
        .from('team_coaches')
        .insert(insertData)
        .select(`
          id,
          team_id,
          coach_id,
          role,
          is_active,
          profiles!inner(id, first_name, last_name, email)
        `);

      if (error) {
        console.error('❌ Database insertion error:', error);
        
        // More specific error messages based on error codes
        if (error.code === '23503') {
          toast.error('Foreign key constraint error - coach or team not found');
        } else if (error.code === '23505') {
          toast.error('Coach is already assigned to this team');
        } else {
          toast.error(`Database error: ${error.message}`);
        }
        return false;
      }

      if (!data || data.length === 0) {
        toast.error('No data returned from coach assignment');
        return false;
      }

      toast.success('Coach added successfully');
      
      // Refresh the team coaches list
      await fetchTeamCoaches();
      
      // Invalidate related queries
      await queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      
      return true;
    } catch (error) {
      console.error('💥 Unexpected error in addCoach function:', error);
      
      if (error instanceof Error) {
        toast.error(`Failed to add coach: ${error.message}`);
      } else {
        toast.error('Failed to add coach due to an unexpected error');
      }
      return false;
    }
  };

  const removeCoach = async (teamCoachId: string) => {
    try {
      
      const { error } = await supabase
        .from('team_coaches')
        .delete()
        .eq('id', teamCoachId);

      if (error) {
        console.error('Error deleting coach:', error);
        throw error;
      }
      
      toast.success('Coach removed successfully');
      await fetchTeamCoaches();
      await queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    } catch (error) {
      console.error('Error deleting coach:', error);
      toast.error('Failed to remove coach');
    }
  };

  const updateCoachRole = async (teamCoachId: string, newRole: string) => {
    try {
      
      const { error } = await supabase
        .from('team_coaches')
        .update({ role: newRole })
        .eq('id', teamCoachId);

      if (error) {
        console.error('Error updating coach role:', error);
        throw error;
      }
      
      toast.success('Coach role updated successfully');
      await fetchTeamCoaches();
    } catch (error) {
      console.error('Error updating coach role:', error);
      toast.error('Failed to update coach role');
    }
  };

  useEffect(() => {
    fetchTeamCoaches();
  }, [fetchTeamCoaches]);

  return {
    coaches,
    loading,
    addCoach,
    removeCoach,
    updateCoachRole,
    refetch: fetchTeamCoaches
  };
};
